import type { PhantomType } from '@moojo/phantom-type'
import { z } from 'zod'

/**
 * The ID of a user in a social network.
 */
export type ContributionId = PhantomType<string, 'ContributionId'>
export const ContributionId = {
  /**
   * Constructs a ContributionId value
   * @param kind the type of the contribution (which social network, which type of media)
   * @param value the social network's native ID of the media
   * @returns a ContributionId
   */
  make,
  /**
   * Deconstructs a ContribuionId back to its original parts
   * @param contributionId the value to deconstruct
   * @returns an object containing a contribution kind and the social network's native ID of the media
   */
  unmake,
  zod: z.string().transform(s => fromObject(unmake(s as ContributionId))),
}

/**
 * Describes the different types contributions (which social network, which type of media)
 */
export type ContributionKind = 'tiktokVideo'

function make(kind: ContributionKind, value: string) {
  return fromObject({ kind, value })
}

function fromObject(input: { kind: ContributionKind; value: string }) {
  let value = input.value
  value = value.trim()
  if (!value) {
    throw new Error(`empty value cannot be used as ContributionId`)
  }
  const abbreviation = abbreviations[input.kind]
  return `${abbreviation}:${value}` as ContributionId
}

function unmake(contributionId: ContributionId) {
  const parts = contributionId.split(':')
  if (parts.length !== 2) {
    throw new Error(`Bad ContributionId value ("${contributionId}")`)
  }

  const kind = kindByAbbreviation.get(parts[0])
  if (!kind) {
    throw new Error(`unrecognized abbreviation: ${parts[0]}`)
  }

  return { kind, value: parts[1] }
}

const abbreviations: Record<ContributionKind, string> = {
  tiktokVideo: 'TTV',
}

const kindByAbbreviation = new Map<string, ContributionKind>(
  Object.entries(abbreviations).map(([k, v]) => [v, k as ContributionKind]),
)
